import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { customerMeApi } from "@/lib/commerce";
import { formatMoney } from "@/lib/format";
import { orderStatusLabel } from "@/lib/orderStatus";
import { useCustomerAuth } from "@/context/CustomerAuthContext";

export default function MyOrders() {
  const { user, loading: authLoading } = useCustomerAuth();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    customerMeApi.orders()
      .then((d) => setRows(d.items || []))
      .catch((e) => setErr(e.response?.data?.error?.message || "Could not load your orders."));
  }, [user, authLoading]);

  if (authLoading) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;
  if (!user) {
    return (
      <div className="p-10 space-y-3">
        <div className="text-sm">Sign in to view your orders.</div>
        <Button asChild><Link to="/customer/login">Sign in</Link></Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="my-orders-root">
      <header className="border-b bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-semibold">FAST FORCE</Link>
          <div className="flex gap-4 text-sm">
            <Link to="/my/orders" className="font-medium underline underline-offset-4">My orders</Link>
            <Link to="/my/enquiries" className="text-muted-foreground hover:text-foreground">My enquiries</Link>
          </div>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-10 space-y-4">
        <h1 className="text-3xl font-semibold">My orders</h1>
        {err && <div className="text-sm text-destructive">{err}</div>}
        {!rows ? (
          <div className="text-sm text-muted-foreground">Loading your orders…</div>
        ) : rows.length === 0 ? (
          <div className="border rounded-lg p-12 text-center text-sm text-muted-foreground" data-testid="my-orders-empty">
            You haven&rsquo;t placed any orders yet.
            <div className="mt-3"><Button asChild><Link to="/catalog">Browse parts</Link></Button></div>
          </div>
        ) : (
          rows.map((o) => (
            <Card key={o.id} data-testid={`my-order-${o.order_number}`}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Order</div>
                  <CardTitle className="font-mono text-base">{o.order_number}</CardTitle>
                  {o.from_inquiry_number && (
                    <div className="text-xs text-emerald-700 mt-1">
                      Converted from enquiry {o.from_inquiry_number}
                    </div>
                  )}
                </div>
                <Badge variant="secondary">{orderStatusLabel(o.status)}</Badge>
              </CardHeader>
              <CardContent className="text-sm space-y-1">
                {(o.items || []).map((it, i) => (
                  <div key={i} className="flex justify-between gap-2">
                    <span className="truncate">{it.name} × {it.qty}</span>
                    <span className="shrink-0">{formatMoney({ amount_minor: it.line_total_minor, currency: it.currency })}</span>
                  </div>
                ))}
                <div className="flex justify-between font-semibold pt-2 border-t">
                  <span>Total</span>
                  <span>{formatMoney({ amount_minor: o.totals?.grand_total_minor || 0, currency: o.totals?.currency || "AED" })}</span>
                </div>
                <div className="text-xs text-muted-foreground pt-1">
                  Placed {new Date(o.created_at).toLocaleString()} · Payment: {(o.payment_method || "").replace(/_/g, " ")}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </main>
    </div>
  );
}
